import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { userService } from "../../../services/api/authenService";
import { toast } from "react-toastify";

const RowButton = ({ icon, label, onClick, className = "", ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center justify-between p-3 rounded-lg-md hover:bg-blue-200 focus:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${className}`}
    aria-label={ariaLabel || label}
  >
    <span className="flex gap-3 items-center">
      <img src={icon} alt="" className="size-[24px]" />
      <span className="text-left">{label}</span>
    </span>
    <img src="/icons/right-arrow-black.svg" alt="" aria-hidden="true" />
  </button>
);

const AccountController = () => {
  const navigate = useNavigate();
  const { user_client, dispatch } = useContext(AuthContext);

  // guard để tránh double click logout
  const [loggingOut, setLoggingOut] = useState(false);
  const mountedRef = useRef(true);

  const safeUserName =
    user_client?.username ||
    user_client?.fullName ||
    user_client?.companyName ||
    "Tài khoản";

  const roleLabel = useMemo(() => {
    switch (user_client?.role) {
      case "Staff":
        return "Nhân viên";
      case "Manager":
        return "Quản lý";
      default:
        return "Cộng tác viên";
    }
  }, [user_client?.role]);

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await userService.log_out();
      dispatch({ type: "LOGOUT" });
      toast.success("Đăng xuất thành công!");
      navigate("/login", { replace: true });
    } catch (error) {
      toast.dismiss();
      toast.error("Đăng xuất không thành công!");
      console.error("Error:", error);
    } finally {
      if (mountedRef.current) setLoggingOut(false);
    }
  }, [dispatch, navigate, loggingOut]);

  // cleanup khi unmount
  // (tránh setState khi đã rời trang)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useState(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const menu = useMemo(
    () => [
      {
        icon: "/icons/user-profile.svg",
        label: "Thông tin hồ sơ",
        onClick: () => navigate("/tai-khoan-cua-toi"),
        top: true,
      },
      {
        icon: "/icons/user-group.svg",
        label: "Tài khoản nhân viên",
        onClick: () => navigate("/tai-khoan-nhan-vien"),
      },
      {
        icon: "/icons/setting.svg",
        label: "Đặt lại mật khẩu",
        onClick: () => navigate("/dat-lai-mat-khau"),
      },
      {
        icon: "/icons/law.svg",
        label: "Chính sách",
        onClick: () => navigate("/chinh-sach"),
      },
    ],
    [navigate]
  );

  return (
    <div className="h-full w-screen pt-[90px]">
      <div className="w-full h-full bg-white rounded-2xl p-3 relative">
        {/* Header avatar + tên */}
        <div className="absolute -top-10 left-0 right-0 flex items-center justify-center">
          <img
            src={user_client?.avatar || "/images/avatar-default.png"}
            alt="Ảnh đại diện"
            className="rounded-full size-[100px] object-cover border-4 border-white"
          />
        </div>

        <div className="flex items-center justify-center flex-col mt-12">
          <h1 className="text-[#0267FF] text-[20px] font-sf-semibold">
            {safeUserName}
          </h1>
          <p className="opacity-70">{roleLabel}</p>
        </div>

        <div className="h-[calc(100%-170px)] overflow-auto">
          {/* Hàng trên cùng (thông tin hồ sơ) */}
          <div className="mt-3">
            <RowButton
              icon="/icons/user-profile.svg"
              label="Thông tin hồ sơ"
              onClick={() => navigate("/tai-khoan-cua-toi")}
              className="rounded-lg shadow border"
            />
          </div>

          {/* Card nhóm menu còn lại */}
          <div className="w-full border rounded-lg mt-3 overflow-hidden">
            {menu.slice(1).map((item, idx) => (
              <RowButton
                key={item.label}
                icon={item.icon}
                label={item.label}
                onClick={item.onClick}
                className={`${
                  idx < menu.length - 1 ? "shadow" : "rounded-none"
                }`}
              />
            ))}

            {/* Logout */}
            <button
              type="button"
              onClick={handleLogout}
              disabled={loggingOut}
              className={`w-full flex items-center justify-between p-3 hover:bg-red-200 focus:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-400 transition ${
                loggingOut ? "opacity-60 cursor-default" : ""
              }`}
              aria-label="Đăng xuất"
            >
              <span className="flex gap-3 items-center text-[#DE2626]">
                <img src="/icons/logout.svg" alt="" className="size-[24px]" />
                <span>Đăng xuất</span>
              </span>
              <img src="/icons/right-arrow-red.svg" alt="" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountController;
